import { useEffect } from "react";

type Props = {
  images: string[];
  index: number | null;
  title?: string;
  onClose: () => void;
  onChange: (index: number) => void;
};

export default function Lightbox({ images, index, title, onClose, onChange }: Props) {
  const open = index !== null;

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowRight") onChange(((index as number) + 1) % images.length);
      if (event.key === "ArrowLeft") onChange(((index as number) - 1 + images.length) % images.length);
    };
    document.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [open, index, images.length, onClose, onChange]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="剧照预览"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-background-50/95 px-4 py-10 backdrop-blur-sm"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="关闭预览"
        className="absolute right-4 top-4 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-400/60 text-foreground-200 transition-colors hover:border-accent-500 hover:text-accent-400"
      >
        <i className="ri-close-line text-lg" />
      </button>

      <button
        type="button"
        aria-label="上一张"
        onClick={(event) => {
          event.stopPropagation();
          onChange(((index as number) - 1 + images.length) % images.length);
        }}
        className="absolute left-2 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-background-400/60 bg-background-100/70 text-foreground-200 transition-colors hover:border-accent-500 hover:text-accent-400 md:left-6"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>

      <figure className="max-h-full w-full max-w-4xl" onClick={(event) => event.stopPropagation()}>
        <div className="flex w-full items-center justify-center overflow-hidden rounded-lg bg-background-200">
          <img
            src={images[index as number]}
            alt={`${title || "影片"} 剧照 ${(index as number) + 1}`}
            className="max-h-[72vh] w-full object-contain"
          />
        </div>
        <figcaption className="mt-3 flex items-center justify-between gap-3 text-[12px] text-foreground-400 font-label">
          <span>{title} · 剧照 {(index as number) + 1}</span>
          <span className="text-foreground-600">
            {(index as number) + 1} / {images.length}
          </span>
        </figcaption>
      </figure>

      <button
        type="button"
        aria-label="下一张"
        onClick={(event) => {
          event.stopPropagation();
          onChange(((index as number) + 1) % images.length);
        }}
        className="absolute right-2 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-background-400/60 bg-background-100/70 text-foreground-200 transition-colors hover:border-accent-500 hover:text-accent-400 md:right-6"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>
    </div>
  );
}